import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { addressContext } from 'features/conversation-preferences/address-form';
import { useAddressForm } from 'features/conversation-preferences/hooks/use-conversation-preferences';
import { useSoulEditor } from '../hooks/use-soul-editor';
import type { AgentSoulSource } from '../services/agent-soul-service';
import { SoulConfirmDialog } from './soul-confirm-dialog';
import { SoulActions, SoulAlert, SoulField, SoulLoading } from './soul-parts';

type AgentSoulSectionProps = {
  agentId: string;
  /** False for viewers without edit rights; the backend's `editable` must agree too. */
  canEdit?: boolean;
};

/** Buddy's note for Buddy; the default-soul note only where the user can write their own. */
function useSoulNote(source: AgentSoulSource | undefined, isEditable: boolean) {
  const { t } = useTranslation();
  const context = addressContext(useAddressForm());
  if (source === 'platform_buddy') {
    return t('AGENT_SOUL_BUDDY_NOTE', {
      defaultValue: "Buddy's soul is maintained by Klax and can't be edited.",
    });
  }
  if (source !== 'platform_default' || !isEditable) return null;
  return t('AGENT_SOUL_DEFAULT_NOTE', {
    defaultValue: "This agent uses Klax's default soul. Write your own to replace it.",
    context,
  });
}

/**
 * The "Soul" section of the agent configuration: the agent's SOUL.md with a counter, save and
 * restore-default. Hidden when souls are off or the soul API is missing; read-only for viewers,
 * for Buddy, and when the agent can no longer be read or changed.
 */
export function AgentSoulSection({ agentId, canEdit = true }: Readonly<AgentSoulSectionProps>) {
  const { t } = useTranslation();
  const fieldId = useId();
  const editor = useSoulEditor(agentId, canEdit);
  const [isReloadDialogOpen, setIsReloadDialogOpen] = useState(false);
  const note = useSoulNote(editor.soul?.source, editor.isEditable);
  const isBuddy = editor.soul?.source === 'platform_buddy';

  if (editor.isHidden) return null;
  if (editor.isLoading) return <SoulLoading testId="agent-soul-loading" />;
  // C9: a failed read stays inside this section, with a retry.
  if (!editor.soul && !editor.isReadOnlyError) {
    return (
      <SoulAlert
        isError
        message={editor.failedText}
        actionLabel={t('RETRY', { defaultValue: 'Retry' })}
        onAction={editor.retry}
      />
    );
  }

  const reload = () => {
    setIsReloadDialogOpen(false);
    editor.reload();
  };
  const reloadLabel = t('AGENT_SOUL_RELOAD', { defaultValue: 'Reload' });
  // C4: an unsaved draft is replaced only after the user confirms.
  const askReload = () => (editor.isDirty ? setIsReloadDialogOpen(true) : reload());

  return (
    <div className="space-y-4" data-testid="agent-soul-section">
      {isBuddy ? null : (
        <p className="text-sm text-muted-foreground">
          {t('AGENT_SOUL_HINT', {
            defaultValue:
              'Who this agent is: personality, tone and values. What it should do belongs in the prompt.',
          })}
        </p>
      )}

      {editor.isReadOnlyError ? <SoulAlert isError message={editor.failedText} /> : null}
      {note ? <SoulAlert message={note} /> : null}
      {editor.isConflict ? (
        <SoulAlert
          isError
          message={t('AGENT_SOUL_CONFLICT', {
            defaultValue:
              'Someone else changed the soul in the meantime. Reload to see the current version.',
          })}
          actionLabel={reloadLabel}
          onAction={askReload}
        />
      ) : null}

      <SoulField
        id={fieldId}
        value={editor.value}
        isEditable={editor.isEditable}
        isShownOnly={isBuddy}
        count={editor.count}
        isTooLong={editor.isTooLong}
        error={editor.fieldError}
        onChange={editor.change}
      />

      {editor.isEditable ? (
        <SoulActions
          canReset={!editor.isSaving && (editor.soul?.revision !== null || editor.isDirty)}
          canSave={!editor.isSaving && editor.isDirty && !editor.fieldError}
          onReset={editor.reset}
          onSave={editor.save}
        />
      ) : null}

      <SoulConfirmDialog
        open={isReloadDialogOpen}
        title={t('AGENT_SOUL_RELOAD_TITLE', { defaultValue: 'Load the current version?' })}
        description={t('AGENT_SOUL_RELOAD_DESCRIPTION', {
          defaultValue: 'The unsaved text will be replaced.',
        })}
        confirmLabel={reloadLabel}
        onConfirm={reload}
        onCancel={() => setIsReloadDialogOpen(false)}
      />
      <SoulConfirmDialog
        open={editor.leave.isLeaveDialogOpen}
        title={t('AGENT_SOUL_LEAVE_TITLE', { defaultValue: 'Discard unsaved changes?' })}
        description={t('AGENT_SOUL_LEAVE_DESCRIPTION', {
          defaultValue: 'The soul has unsaved changes. They will be lost if you leave now.',
        })}
        confirmLabel={t('AGENT_SOUL_LEAVE_CONFIRM', { defaultValue: 'Discard' })}
        onConfirm={editor.leave.confirmLeave}
        onCancel={editor.leave.cancelLeave}
      />
    </div>
  );
}
